import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import MessagesTable from "../components/MessagesTable";
import MessageDetail from "../components/MessageDetail";
import MessageSidebar from "../components/MessagesSideBar";
import ComposeMessage from "../components/ComposeMessage";
import { useMessages } from "../hooks/useMessages";
import { Message } from "../../../../types/messageTypes";
import PageHeader from "components/Common/PageHeader";
import CustomButton from "components/Common/ui/CustomButton";
import Loader from "components/Common/loader";
import { useAbility } from "config/abilityContext";
import { useMessagingSettings } from "feature-module/settings/hooks/useMessagingSettings";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import CustomAlert from "components/Common/ui/CustomAlert"; // 🔹 adjust path if needed

const MessagesPage: React.FC = () => {
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [selectedType, setSelectedType] = useState<"inbox" | "sent" | "getAll">(
    "inbox"
  );
  const [isComposing, setIsComposing] = useState<boolean>(false);
  const ability = useAbility();
  const { user } = useSelector((s: RootState) => s.authSlice);
  const role = user?.role || "guest";

  const { messagingConfig, fetchMessagingSettings } = useMessagingSettings();

  const { messages, pagination, loading, error, setPage, setLimit } =
    useMessages(selectedType);

  const { t } = useTranslation();

  useEffect(() => {
    fetchMessagingSettings();
  }, []);

  // 🔹 Check if this role has at least one allowed target in messagingConfig
  const hasMessaging = useMemo(() => {
    if (!messagingConfig) return false;
    const roleConfig =
      messagingConfig[role as keyof typeof messagingConfig] || null;
    return !!roleConfig && Array.isArray(roleConfig.canMessage) && roleConfig.canMessage.length > 0;
  }, [messagingConfig, role]);

  const canCompose = ability.can("create", "Message") && hasMessaging;

  const handleTypeChange = (type: "inbox" | "sent" | "getAll") => {
    setSelectedType(type);
    setIsComposing(false);
    setSelectedMessage(null);
  };

  const renderSidebar = () => (
    <MessageSidebar
      selectedType={selectedType}
      onTypeChange={handleTypeChange}
    />
  );

  const renderMessagesTable = () => (
    <MessagesTable
      messages={messages}
      pagination={pagination}
      onPageChange={(page, pageSize) => {
        setPage(page);
        setLimit(pageSize);
      }}
      onMessageSelect={setSelectedMessage}
    />
  );

  const renderMessageDetail = () => (
    <MessageDetail
      message={selectedMessage as Message}
      onBack={() => setSelectedMessage(null)}
    />
  );

  const renderComposeEmail = () => (
    <ComposeMessage onBack={() => setIsComposing(false)} />
  );

  const renderContent = () => {
    if (isComposing && canCompose) {
      return renderComposeEmail();
    }

    if (loading) {
      return <Loader />;
    }

    if (error) {
      return <p className="text-danger">{error}</p>;
    }

    return selectedMessage ? renderMessageDetail() : renderMessagesTable();
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader
          title={t("messaging")}
          breadcrumbItems={[
            { label: "dashboard", path: "/dashboard" },
            { label: "messaging", active: true },
          ]}
        />

        <div className="mb-3 d-flex justify-content-end gap-2">
          {canCompose ? (
            <CustomButton
              onClick={() => setIsComposing(true)}
              variant="primary"
            >
              <i className="ti ti-plus me-2" />
              {t("add_messaging")}
            </CustomButton>
          ) : (
              <CustomAlert
                type="info"
                className="w-100"
                message={t("alert.you_cant_create_messaging.message")}
                description={t(
                  "alert.you_cant_create_messaging.description"
                )}
                showIcon
              />
            
          )}
        </div>

        {renderSidebar()}
        <div className="mt-3">{renderContent()}</div>
      </div>
    </div>
  );
};

export default MessagesPage;